import { z } from 'zod';

/**
 * SIL-1914 (db/schema/1090_asset_unit_serials_sil_1914.sql) - per-unit serials
 * of a stock line (asset.quantity > 1). Partial entry is valid: unitsCaptured
 * may be anything from 0 to quantity.
 *
 * SIL-1269: a unit serial follows the asset-serial rule. NO schema in this file
 * carries a serial in bulk. client_readonly has no column privilege on
 * asset_unit_serial.serial/normalized_serial, so the database enforces the same
 * thing. The audited per-item reveal (and its response schema's use) lands with
 * the entry issue (SIL-1916); `assetUnitSerialRevealSchema` is declared here so
 * the type exists, but no client list/detail payload may include it.
 */
export const assetUnitSourceSchema = z.enum(['manual', 'camera']);
export type AssetUnitSource = z.infer<typeof assetUnitSourceSchema>;

/** One captured unit, serial masked. capturedBy is deliberately not exposed. */
export const assetUnitSchema = z.object({
  id: z.string().uuid(),
  assetId: z.string().uuid(),
  source: assetUnitSourceSchema,
  capturedAt: z.string(),
});
export type AssetUnit = z.infer<typeof assetUnitSchema>;

/** Capture progress for a stock line: unitsCaptured <= quantity. */
export const assetUnitLineSummarySchema = z.object({
  assetId: z.string().uuid(),
  quantity: z.number().int().positive(),
  unitsCaptured: z.number().int().nonnegative(),
});
export type AssetUnitLineSummary = z.infer<typeof assetUnitLineSummarySchema>;

/** Response of the future audited per-item reveal (SIL-1916). Never in a list. */
export const assetUnitSerialRevealSchema = z.object({
  id: z.string().uuid(),
  serial: z.string(),
});
export type AssetUnitSerialReveal = z.infer<typeof assetUnitSerialRevealSchema>;

// ---------------------------------------------------------------------------
// SIL-1916 - manual entry of unit serials (migration 1095)
// ---------------------------------------------------------------------------

/** Lines per add call. Mirrors client_add_asset_unit_serials (too_many_serials). */
export const UNIT_SERIAL_MAX_BATCH = 50;
/** Trimmed length bound; the table CHECK is 64 trimmed / 128 raw (1090). */
export const UNIT_SERIAL_MAX_LENGTH = 64;
/** Per-element hard cap at the request boundary (anything longer is hostile, not a typo). */
export const UNIT_SERIAL_REQUEST_ELEMENT_MAX = 256;
/** Service tags: alphanumeric start, then letters, digits and . _ / -. No inner whitespace. */
export const UNIT_SERIAL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]*$/;

/** Per-row reasons from client_add_asset_unit_serials / client_replace_asset_unit_serial. */
export const assetUnitRowReasonSchema = z.enum([
  'blank',
  'too_long',
  'invalid_chars',
  'duplicate_in_request',
  'already_on_line',
  'exceeds_quantity',
  'in_use_in_org',
]);
export type AssetUnitRowReason = z.infer<typeof assetUnitRowReasonSchema>;

/** Customer-facing text per row reason (single source for API errors and the bulk result panel). */
export const ASSET_UNIT_ROW_REASON_MESSAGES: Record<AssetUnitRowReason, string> = {
  blank: 'Serial is empty.',
  too_long: 'Serial is longer than 64 characters.',
  invalid_chars: 'Use letters, digits and . _ / - only (no spaces).',
  duplicate_in_request: 'Appears more than once in this list.',
  already_on_line: 'Already entered on this line.',
  exceeds_quantity: 'All units on this line already have a serial.',
  in_use_in_org: 'Already used by another asset in your organization.',
};

/** Same rules as the database function, for the UI and the API boundary. Returns the reason or null. */
export function validateUnitSerial(raw: string | null | undefined): AssetUnitRowReason | null {
  if (raw == null || raw.trim() === '') return 'blank';
  if (raw.length > 128 || raw.trim().length > UNIT_SERIAL_MAX_LENGTH) return 'too_long';
  if (!UNIT_SERIAL_PATTERN.test(raw.trim())) return 'invalid_chars';
  return null;
}

export const assetUnitAddRequestSchema = z.object({
  // A bad ELEMENT must not fail the whole request (per-line results); only
  // the array size and a hostile element length are boundary errors.
  serials: z.array(z.string().max(UNIT_SERIAL_REQUEST_ELEMENT_MAX)).min(1).max(UNIT_SERIAL_MAX_BATCH),
  /** Client-asserted provenance; proves nothing. SIL-1917 sends 'camera'. */
  source: assetUnitSourceSchema.default('manual'),
});
export type AssetUnitAddRequest = z.input<typeof assetUnitAddRequestSchema>;

export const assetUnitReplaceRequestSchema = z.object({
  serial: z.string().max(UNIT_SERIAL_REQUEST_ELEMENT_MAX),
  source: assetUnitSourceSchema.default('manual'),
});
export type AssetUnitReplaceRequest = z.input<typeof assetUnitReplaceRequestSchema>;

export const assetUnitRowResultSchema = z.object({
  /** 1-based position in the submitted `serials` array. */
  index: z.number().int().positive(),
  status: z.enum(['accepted', 'rejected']),
  reason: assetUnitRowReasonSchema.optional(),
  unitId: z.string().uuid().optional(),
});
export type AssetUnitRowResult = z.infer<typeof assetUnitRowResultSchema>;

/** Serials are never echoed back; the caller maps `index` to its own input. */
export const assetUnitAddResponseSchema = z.object({
  quantity: z.number().int().positive(),
  unitsCaptured: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  results: z.array(assetUnitRowResultSchema),
});
export type AssetUnitAddResponse = z.infer<typeof assetUnitAddResponseSchema>;

export const assetUnitListResponseSchema = z.object({
  summary: assetUnitLineSummarySchema,
  /** Sold lines are read-only history. */
  locked: z.boolean(),
  units: z.array(assetUnitSchema),
});
export type AssetUnitListResponse = z.infer<typeof assetUnitListResponseSchema>;
